/**
 * pz-new-relic.js — подключает New Relic (видео сессий и ошибки JS) только
 * после согласия посетителя на аналитику (PZ-555).
 *
 * Зачем. Ошибки JS на витрине не ловит никто: Sentry стоит только на наших
 * воркерах. New Relic пишет видео сессии и кладёт рядом ошибку со стеком.
 *
 * Почему агент грузится отсюда, а не кодом New Relic в <head>:
 *  - приватность: даже в browser_consent_mode агент ещё ДО согласия пишет
 *    сессию на устройство (NRBA_SESSION в localStorage — замер 21.09), а в
 *    ЕС/UK это можно только после «Принять» в баннере Shopify;
 *  - скорость: загрузчик весит 78 КБ (27 КБ сжатого) — в HTML он ехал бы с
 *    каждой страницей и делил канал с обложкой (PZ-425); с CDN New Relic
 *    он кэшируется на год (max-age=31536000);
 *  - надёжность: загрузчик async, вне очереди скриптов темы — зависший CDN
 *    New Relic не держит «В корзину».
 * Цена (дока New Relic об отложенном запуске): ошибки и запросы первых
 * секунд страницы, до запуска агента, не попадут.
 *
 * Согласие — Shopify Customer Privacy API: где баннер не нужен,
 * analyticsProcessingAllowed() сразу true (замер 21.09 из UA); где нужен —
 * после «Принять», ответ приходит событием visitorConsentCollected.
 * «Отклонить» у запущенного агента — newrelic.consent(false): агент в
 * browser_consent_mode, отправка встаёт. Сбой API — агент не грузится.
 *
 * Обновить агент = версия в NEW_RELIC_LOADER.url и новый хэш:
 *   curl -s <url> | openssl dgst -sha384 -binary | openssl base64 -A
 * Куски агента загрузчик сам тянет с того же хоста с проверкой хэша.
 * licenseKey — публичный браузерный ключ только на запись, как DSN Sentry.
 */

// Код, выданный New Relic (Integrations & Agents → Browser monitoring →
// copy/paste, приложение 538904771, регион EU), с нашими правками.
export const NEW_RELIC_SETTINGS = {
  init: {
    browser_consent_mode: { enabled: true },
    privacy: { cookies_enabled: true },
    // Бэкенд не в New Relic: заголовки трассировки уходили бы в каждый
    // запрос на свой домен, включая POST /cart/add.js, — без пользы
    distributed_tracing: { enabled: false },
    performance: { capture_measures: true },
    ajax: { deny_list: ['bam.eu01.nr-data.net'], capture_payloads: 'none' },
    session_replay: {
      enabled: true,
      sampling_rate: 100,
      error_sampling_rate: 100,
      // Текст витрины публичный; по умолчанию New Relic прячет его звёздочками
      mask_text_selector: null,
      // Поля ввода (почта, формы) — скрыты
      mask_all_inputs: true,
    },
  },
  loader_config: {
    accountID: '8536137',
    trustKey: '8536137',
    agentID: '538904771',
    licenseKey: 'NRJS-30577a26ae51315a72d',
    applicationID: '538904771',
  },
  info: {
    beacon: 'bam.eu01.nr-data.net',
    errorBeacon: 'bam.eu01.nr-data.net',
    licenseKey: 'NRJS-30577a26ae51315a72d',
    applicationID: '538904771',
    sa: 1,
  },
};

// Pro, а не Pro+SPA: переходы по страницам витрины — обычные загрузки
export const NEW_RELIC_LOADER = {
  url: 'https://js-agent.newrelic.com/nr-loader-full-1.322.0.min.js',
  integrity: 'sha384-Cjach/26JgiQ3J1pjt8ECNT0DAqFkRzmnf6hxiqd4oYe6toLTJMPT/XfRVvcLYcW',
};

const CONSENT_API_FEATURES = [{ name: 'consent-tracking-api', version: '0.1' }];
const CONSENT_COLLECTED_EVENT = 'visitorConsentCollected';
const STATE_KEY = 'pzNewRelic';

export function isAnalyticsAllowed(win) {
  return win.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
}

function getState(win) {
  win[STATE_KEY] ??= { isLoaderAdded: false, isAllowed: false };
  return win[STATE_KEY];
}

// Передать агенту текущее решение. Пока загрузчик не исполнился, агента
// нет — решение передаст обработчик load (посетитель мог передумать, пока
// файл грузился)
function syncConsent(win) {
  win.newrelic?.consent?.(getState(win).isAllowed);
}

function addLoader(win) {
  // Копия: агент дописывает в NREUM свои поля, константа должна остаться чистой
  win.NREUM = structuredClone(NEW_RELIC_SETTINGS);
  const loader = win.document.createElement('script');
  loader.src = NEW_RELIC_LOADER.url;
  loader.integrity = NEW_RELIC_LOADER.integrity;
  loader.crossOrigin = 'anonymous';
  loader.async = true;
  loader.addEventListener('load', () => syncConsent(win), { once: true });
  win.document.head.appendChild(loader);
}

function applyConsentDecision(win, isAllowed) {
  const state = getState(win);
  state.isAllowed = isAllowed;
  // Без согласия агент не грузим вовсе: он пишет сессию на устройство
  if (isAllowed && !state.isLoaderAdded) {
    state.isLoaderAdded = true;
    addLoader(win);
    return;
  }
  syncConsent(win);
}

export function installNewRelic(win) {
  win.document.addEventListener(CONSENT_COLLECTED_EVENT, (event) => {
    applyConsentDecision(win, event.detail?.analyticsAllowed === true);
  });
  if (typeof win.Shopify?.loadFeatures !== 'function') return;
  win.Shopify.loadFeatures(CONSENT_API_FEATURES, (error) => {
    if (error) {
      console.warn('pz-new-relic:', error);
      return;
    }
    applyConsentDecision(win, isAnalyticsAllowed(win));
  });
}

if (typeof window !== 'undefined') installNewRelic(window);
